"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import {
  HiOutlineBookOpen,
  HiOutlineMicrophone,
  HiOutlinePencilAlt,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MdOutlineChat } from "react-icons/md";
import { IoGameControllerOutline } from "react-icons/io5";
import { TbCategory2 } from "react-icons/tb";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import { areWhackAMoleItemsSaveReady } from "../shared/mediaValidation";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import correctAnswerAPI from "../../../services/correctAnswer/correctAnswerAPI";
import { MEDIA_BTN_DELETE_MD } from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import { GamePreviewModal } from "../shared/GamePreviewModal";
import {
  createWhackAMoleItemsGameSnapshot,
  getCanGamePreview,
  getShouldConfirmLeaveGameEditor,
  patchSavedWhackAMoleItemsSnapshotAfterItemRemoved,
  patchSavedWhackAMoleItemsSnapshotAfterMediaDeleted,
  PREVIEW_BUTTON_DISABLED_TITLE,
  GAME_PREVIEW_BUTTON_CLASS,
  PREVIEW_UNSAVED_MESSAGE,
  PREVIEW_UNSAVED_TITLE,
  useHasUnsavedGameFormChanges,
} from "../shared/gameFormSnapshot";
import { useReportGameEditorLeaveGuard } from "../shared/GameEditorLeaveGuard";
import {
  createGameFieldErrorPopUp,
  type GameStatusPopUpState,
  useGameFieldErrorHighlight,
  whackAMoleItemFieldTarget,
} from "../shared/fieldErrorHighlight";
import { useTranslations } from "next-intl";

type CorrectAnswerItem = {
  id: number;
  no: number;
  correctAnswer: string;
  wrongAnswer: string;
  imageCorrectAnswerFileName: string;
  imageCorrectAnswerFile: File | null;
  imageCorrectAnswerUrl: string | null;
  soundCorrectAnswerFileName: string;
  soundCorrectAnswerFile: File | null;
  soundCorrectAnswerUrl: string | null;
  imageWrongAnswerFileName: string;
  imageWrongAnswerFile: File | null;
  imageWrongAnswerUrl: string | null;
  soundWrongAnswerFileName: string;
  soundWrongAnswerFile: File | null;
  soundWrongAnswerUrl: string | null;
  hasHint: boolean;
  hintText: string;
  hintImageFileName: string;
  hintImageFile: File | null;
  hintImageUrl: string | null;
  hintAudioFileName: string;
  hintAudioFile: File | null;
  hintAudioUrl: string | null;
};

type WhackAMoleMediaDeleteField =
  | "sound_correct_answer"
  | "sound_wrong_answer"
  | "image_correct_answer"
  | "image_wrong_answer";

type PendingMediaDelete = {
  itemId: number;
  field: WhackAMoleMediaDeleteField;
};

type UploadTarget = {
  key: string;
  file: File;
  fileName: string;
  sectionName: string;
  refId: string;
};

type GameSkills = {
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
  skill_listening_speaking: false,
  skill_lang_nature: false,
  skill_reading: false,
  skill_writing: false,
};

type GameAudios = {
  audio_question: boolean;
  audio_choice: boolean;
  audio_assistant_voice: boolean;
  audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

const DEFAULT_GAME_AUDIOS: GameAudios = {
  audio_question: false,
  audio_choice: false,
  audio_assistant_voice: false,
  audio_background: false,
};

type WhackAMoleProps = {
  createdGameData?: {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string | null;
    status?: boolean | "true" | "false" | number | string | null;
  } | null;
  initialGameDetail?: {
    game_info?: {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    };
    questions?: Array<{
      id?: number;
      no?: number;
      keyword?: string;
      question?: string;
      matching_word?: string;
      answer?: string;
      hint?: string | null;
      image_keyword?: string | null;
      sound_keyword?: string | null;
      image_matching_word?: string | null;
      sound_matching_word?: string | null;
      correct_answer?: string | null;
      wrong_answer?: string | null;
      image_correct_answer?: string | null;
      sound_correct_answer?: string | null;
      image_wrong_answer?: string | null;
      sound_wrong_answer?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
    }>;
  } | null;
  onCompleted?: () => void;
};

const MIN_ITEMS = 2;
const MAX_ITEMS = 5;

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return "";
  }
  return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
  if (!path) {
    return "";
  }

  const cleanPath = String(path);
  const bySlash = cleanPath.split("/");
  const candidate = bySlash[bySlash.length - 1] || "";
  return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const raw = String(path).trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

function normalizeSkillBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return null;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

function normalizeExpectedTime(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return "0";
  }
  return String(Math.floor(parsed));
}

function getPublishButtonClass(published: boolean): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75";

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

function renderPublishStatusContent(
  published: boolean,
  labels: {
    publish: string;
    unpublish: string;
  },
  iconSize = 16,
) {
  if (published) {
    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#16A34A" />
        </span>
        <span className="text-[#16A34A]">{labels.publish}</span>
      </>
    );
  }

  return (
    <>
      <span className="inline-flex shrink-0 items-center justify-center">
        <BiWorld size={iconSize} color="#3b3b3b" />
      </span>
      <span className="text-[#3b3b3b]">{labels.unpublish}</span>
    </>
  );
}

function getStatusToggleDialogContent(
  currentStatus: boolean,
  labels: {
    confirmPublish: string;
    publishPrompt: string;
    published: string;
    publishedMessage: string;
    confirmUnpublish: string;
    unpublishPrompt: string;
    unpublished: string;
    unpublishedMessage: string;
  },
): StatusToggleDialogContent {
  const nextStatus = !currentStatus;

  if (nextStatus) {
    return {
      confirmTitle: labels.confirmPublish,
      confirmMessage: labels.publishPrompt,
      successTitle: labels.published,
      successMessage: labels.publishedMessage,
    };
  }

  return {
    confirmTitle: labels.confirmUnpublish,
    confirmMessage: labels.unpublishPrompt,
    successTitle: labels.unpublished,
    successMessage: labels.unpublishedMessage,
  };
}

function normalizeGameSkillsOrNull(
  value: Partial<Record<GameSkillKey, unknown>>,
) {
  const listening = normalizeSkillBoolean(value.skill_listening_speaking);
  const langNature = normalizeSkillBoolean(value.skill_lang_nature);
  const reading = normalizeSkillBoolean(value.skill_reading);
  const writing = normalizeSkillBoolean(value.skill_writing);

  if (
    listening === null ||
    langNature === null ||
    reading === null ||
    writing === null
  ) {
    return null;
  }

  return {
    skill_listening_speaking: listening,
    skill_lang_nature: langNature,
    skill_reading: reading,
    skill_writing: writing,
  };
}

function extractGameSkillsFromUnknown(
  value: Partial<Record<GameSkillKey, unknown>>,
): GameSkills {
  const normalized = normalizeGameSkillsOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function normalizeGameAudiosOrNull(
  value: Partial<Record<GameAudiosKey, unknown>>,
) {
  const audioQuestion = normalizeSkillBoolean(value.audio_question);
  const audioChoice = normalizeSkillBoolean(value.audio_choice);
  const audioAssistantVoice = normalizeSkillBoolean(
    value.audio_assistant_voice,
  );
  const audioBackground = normalizeSkillBoolean(value.audio_background);

  if (
    audioQuestion === null ||
    audioChoice === null ||
    audioAssistantVoice === null ||
    audioBackground === null
  ) {
    return null;
  }

  return {
    audio_question: audioQuestion,
    audio_choice: audioChoice,
    audio_assistant_voice: audioAssistantVoice,
    audio_background: audioBackground,
  };
}

function areGameAudiosEqual(left: GameAudios, right: GameAudios) {
  return (
    left.audio_question === right.audio_question &&
    left.audio_choice === right.audio_choice &&
    left.audio_assistant_voice === right.audio_assistant_voice &&
    left.audio_background === right.audio_background
  );
}

function extractGameAudiosFromUnknown(
  value: Partial<Record<GameAudiosKey, unknown>>,
): GameAudios {
  const normalized = normalizeGameAudiosOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_AUDIOS };
}

function createEmptyCorrectAnswerItem(
  id: number,
  no: number,
): CorrectAnswerItem {
  return {
    id,
    no,
    correctAnswer: "",
    wrongAnswer: "",
    imageCorrectAnswerFileName: "",
    imageCorrectAnswerFile: null,
    imageCorrectAnswerUrl: null,
    soundCorrectAnswerFileName: "",
    soundCorrectAnswerFile: null,
    soundCorrectAnswerUrl: null,
    imageWrongAnswerFileName: "",
    imageWrongAnswerFile: null,
    imageWrongAnswerUrl: null,
    soundWrongAnswerFileName: "",
    soundWrongAnswerFile: null,
    soundWrongAnswerUrl: null,
    hasHint: false,
    hintText: "",
    hintImageFileName: "",
    hintImageFile: null,
    hintImageUrl: null,
    hintAudioFileName: "",
    hintAudioFile: null,
    hintAudioUrl: null,
  };
}

export default function WhackAMole({
  createdGameData,
  initialGameDetail,
  onCompleted,
}: WhackAMoleProps) {
  const tWhack = useTranslations("whackAMole");
  const tMain = useTranslations("main");
  const publishStatusLabels = {
    publish: tWhack("publish"),
    unpublish: tWhack("unpublish"),
  };
  const statusToggleDialogLabels = {
    confirmPublish: tWhack("confirmPublish"),
    publishPrompt: tWhack("publishPrompt"),
    published: tWhack("published"),
    publishedMessage: tWhack("publishedMessage"),
    confirmUnpublish: tWhack("confirmUnpublish"),
    unpublishPrompt: tWhack("unpublishPrompt"),
    unpublished: tWhack("unpublished"),
    unpublishedMessage: tWhack("unpublishedMessage"),
  };
  const nextCorrectAnswerIdRef = useRef(6);
  const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
  const initialSuggestionRef = useRef("");
  const initialDescriptionRef = useRef("");
  const initialCorrectAnswerIdsRef = useRef<Set<number>>(new Set());
  const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });
  const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });
  const savedFormSnapshotRef = useRef<ReturnType<
    typeof createWhackAMoleItemsGameSnapshot
  > | null>(null);

  const [exerciseName, setExerciseName] = useState(
    createdGameData?.exercise_name ?? "",
  );
  const [expectedTime, setExpectedTime] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState<number | null>(
    null,
  );
  const [isUpdateMode, setIsUpdateMode] = useState(
    Array.isArray(initialGameDetail?.questions) &&
      initialGameDetail.questions.length > 0,
  );
  const [isPublished, setIsPublished] = useState(
    normalizeStatus(
      initialGameDetail?.game_info?.status ?? createdGameData?.status ?? false,
    ),
  );
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [statusPopUp, setStatusPopUp] = useState<GameStatusPopUpState | null>(
    null,
  );
  const {
    fieldErrorHighlightKey,
    getFieldBorderClass,
    clearFieldErrorHighlight,
    openStatusPopUp,
    closeStatusPopUp,
  } = useGameFieldErrorHighlight();
  const [pendingMediaDelete, setPendingMediaDelete] =
    useState<PendingMediaDelete | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [gameSkills, setGameSkills] = useState<GameSkills>({
    ...DEFAULT_GAME_SKILLS,
  });
  const [gameAudios, setGameAudios] = useState<GameAudios>({
    ...DEFAULT_GAME_AUDIOS,
  });
  const [items, setItems] = useState<CorrectAnswerItem[]>([
    createEmptyCorrectAnswerItem(1, 1),
    createEmptyCorrectAnswerItem(2, 2),
  ]);

  const canAddItem = items.length < MAX_ITEMS;
  const canRemoveItem = items.length > MIN_ITEMS;

  const previewUuid = String(
    initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? "",
  ).trim();
  useEffect(() => {
    setExerciseName(createdGameData?.exercise_name ?? "");
    setIsPublished(normalizeStatus(createdGameData?.status ?? false));
    initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
  }, [createdGameData?.exercise_name, createdGameData?.status]);

  useEffect(() => {
    if (initialGameDetail?.game_info?.id) {
      return;
    }

    const incomingDescription = String(createdGameData?.description ?? "");
    setDescription(incomingDescription);
    initialDescriptionRef.current = incomingDescription;
  }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

  const applyLoadedGameDetail = (
    detail: WhackAMoleProps["initialGameDetail"],
  ) => {
    if (!detail?.game_info?.id) {
      const createdDescription = String(createdGameData?.description ?? "");
      setSuggestion("");
      setDescription(createdDescription);
      setExpectedTime("");
      initialSuggestionRef.current = "";
      initialDescriptionRef.current = createdDescription;
      initialCorrectAnswerIdsRef.current = new Set();
      initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
      initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
      setGameSkills({ ...DEFAULT_GAME_SKILLS });
      setGameAudios({ ...DEFAULT_GAME_AUDIOS });
      setIsUpdateMode(false);
      savedFormSnapshotRef.current = createWhackAMoleItemsGameSnapshot(
        createdGameData?.exercise_name ?? "",
        "",
        createdDescription,
        { ...DEFAULT_GAME_SKILLS },
        { ...DEFAULT_GAME_AUDIOS },
        [
          createEmptyCorrectAnswerItem(1, 1),
          createEmptyCorrectAnswerItem(2, 2),
        ],
      );
      return;
    }

    const gameInfo = detail.game_info;
    const incomingRows = Array.isArray(detail.questions)
      ? detail.questions
      : [];

    const incomingExerciseName = String(gameInfo.exercise_name ?? "");
    const incomingSuggestion = String(gameInfo.suggestion ?? "");
    const incomingDescription = String(gameInfo.description ?? "");
    const incomingStatus = normalizeStatus(gameInfo.status);

    setExerciseName(incomingExerciseName);
    setSuggestion(incomingSuggestion);
    setDescription(incomingDescription);
    setExpectedTime(
      normalizeExpectedTime(
        (incomingRows[0] as Record<string, unknown> | undefined)?.expected_time,
      ),
    );
    setIsPublished(incomingStatus);
    initialExerciseNameRef.current = incomingExerciseName;
    initialSuggestionRef.current = incomingSuggestion;
    initialDescriptionRef.current = incomingDescription;

    const mappedItems =
      incomingRows.length > 0
        ? incomingRows.map((row, index) => {
            const record = row as Record<string, unknown>;
            const mappedId = Number(record.id ?? index + 1);
            const normalizedId =
              Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
            const correctAnswerRaw = String(
              record.correct_answer ?? record.keyword ?? record.question ?? "",
            );
            const wrongAnswerRaw = String(
              record.wrong_answer ??
                record.matching_word ??
                record.answer ??
                "",
            );
            return {
              id: normalizedId,
              no: Number(record.no ?? index + 1),
              correctAnswer: correctAnswerRaw,
              wrongAnswer: wrongAnswerRaw,
              imageCorrectAnswerFileName: getFileNameFromPath(
                String(
                  record.image_correct_answer ?? record.image_keyword ?? "",
                ),
              ),
              imageCorrectAnswerFile: null,
              imageCorrectAnswerUrl: buildUploadedMediaUrl(
                String(
                  record.image_correct_answer ?? record.image_keyword ?? "",
                ),
              ),
              soundCorrectAnswerFileName: getFileNameFromPath(
                String(
                  record.sound_correct_answer ?? record.sound_keyword ?? "",
                ),
              ),
              soundCorrectAnswerFile: null,
              soundCorrectAnswerUrl: buildUploadedMediaUrl(
                String(
                  record.sound_correct_answer ?? record.sound_keyword ?? "",
                ),
              ),
              imageWrongAnswerFileName: getFileNameFromPath(
                String(
                  record.image_wrong_answer ?? record.image_matching_word ?? "",
                ),
              ),
              imageWrongAnswerFile: null,
              imageWrongAnswerUrl: buildUploadedMediaUrl(
                String(
                  record.image_wrong_answer ?? record.image_matching_word ?? "",
                ),
              ),
              soundWrongAnswerFileName: getFileNameFromPath(
                String(
                  record.sound_wrong_answer ?? record.sound_matching_word ?? "",
                ),
              ),
              soundWrongAnswerFile: null,
              soundWrongAnswerUrl: buildUploadedMediaUrl(
                String(
                  record.sound_wrong_answer ?? record.sound_matching_word ?? "",
                ),
              ),
              hasHint: false,
              hintText: "",
              hintImageFileName: "",
              hintImageFile: null,
              hintImageUrl: null,
              hintAudioFileName: "",
              hintAudioFile: null,
              hintAudioUrl: null,
            };
          })
        : [
            createEmptyCorrectAnswerItem(1, 1),
            createEmptyCorrectAnswerItem(2, 2),
          ];

    const skillsFromGameInfo = extractGameSkillsFromUnknown({
      skill_listening_speaking: gameInfo.skill_listening_speaking,
      skill_lang_nature: gameInfo.skill_lang_nature,
      skill_reading: gameInfo.skill_reading,
      skill_writing: gameInfo.skill_writing,
    });

    const firstRow = incomingRows[0] as Record<string, unknown> | undefined;
    const skillsFromRows = extractGameSkillsFromUnknown({
      skill_listening_speaking: firstRow?.skill_listening_speaking,
      skill_lang_nature: firstRow?.skill_lang_nature,
      skill_reading: firstRow?.skill_reading,
      skill_writing: firstRow?.skill_writing,
    });

    const resolvedSkills =
      incomingRows.length > 0 ? skillsFromRows : skillsFromGameInfo;
    setGameSkills(resolvedSkills);
    initialGameSkillsRef.current = resolvedSkills;

    const audiosFromGameInfo = extractGameAudiosFromUnknown({
      audio_question: gameInfo.audio_question,
      audio_choice: gameInfo.audio_choice,
      audio_assistant_voice: gameInfo.audio_assistant_voice,
      audio_background: gameInfo.audio_background,
    });

    const firstRowForAudio = incomingRows[0] as
      | Record<string, unknown>
      | undefined;
    const audiosFromRows = extractGameAudiosFromUnknown({
      audio_question: firstRowForAudio?.audio_question,
      audio_choice: firstRowForAudio?.audio_choice,
      audio_assistant_voice: firstRowForAudio?.audio_assistant_voice,
      audio_background: firstRowForAudio?.audio_background,
    });

    let resolvedAudios =
      incomingRows.length > 0 ? audiosFromRows : audiosFromGameInfo;
    if (incomingRows.length > 1) {
      for (let index = 1; index < incomingRows.length; index += 1) {
        const row = incomingRows[index] as Record<string, unknown>;
        const rowAudios = normalizeGameAudiosOrNull({
          audio_question: row.audio_question,
          audio_choice: row.audio_choice,
          audio_assistant_voice: row.audio_assistant_voice,
          audio_background: row.audio_background,
        });

        if (!rowAudios) {
          console.warn(
            `[WhackAMole] Invalid audio values in row ${index + 1}, using first row values.`,
          );
          continue;
        }

        if (!areGameAudiosEqual(rowAudios, resolvedAudios)) {
          console.warn(
            "[WhackAMole] Audio values are inconsistent across rows. Using the first row values.",
          );
          break;
        }
      }
    }

    setGameAudios(resolvedAudios);
    initialGameAudiosRef.current = resolvedAudios;

    const normalizedItems = mappedItems.map((item, index) => ({
      ...item,
      no: index + 1,
    }));
    setItems(normalizedItems);

    initialCorrectAnswerIdsRef.current = collectPersistedIdsFromApiRows(
      incomingRows,
      (row) => {
        const mappedId = Number((row as Record<string, unknown>).id);
        return Number.isInteger(mappedId) && mappedId > 0 ? mappedId : null;
      },
    );
    nextCorrectAnswerIdRef.current =
      normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
    setIsUpdateMode(incomingRows.length > 0);
    savedFormSnapshotRef.current = createWhackAMoleItemsGameSnapshot(
      incomingExerciseName,
      incomingSuggestion,
      incomingDescription,
      resolvedSkills,
      resolvedAudios,
      normalizedItems,
    );
  };

  useEffect(() => {
    applyLoadedGameDetail(initialGameDetail);
  }, [initialGameDetail]);

  const setNoByOrder = (sourceItems: CorrectAnswerItem[]) =>
    sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

  const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
    setGameSkills((previous) => ({
      ...previous,
      [skillKey]: checked,
    }));
  };

  const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
    setGameAudios((previous) => ({
      ...previous,
      [audioKey]: checked,
    }));
  };

  const addItem = () => {
    if (!canAddItem) {
      return;
    }
    const newId = nextCorrectAnswerIdRef.current;
    nextCorrectAnswerIdRef.current += 1;
    setItems((previous) =>
      setNoByOrder([
        ...previous,
        createEmptyCorrectAnswerItem(newId, previous.length + 1),
      ]),
    );
  };

  const removeItem = async (id: number) => {
    if (!canRemoveItem) {
      return;
    }
    if (isUpdateMode && initialCorrectAnswerIdsRef.current.has(id)) {
      const deleteResponse = await correctAnswerAPI.hardDeleteCorrectAnswer({
        correctAnswerId: id,
      });
      if (deleteResponse?.status === "failed") {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("deleteError"),
          type: "error",
        });
        return;
      }
      initialCorrectAnswerIdsRef.current.delete(id);
      patchSavedWhackAMoleItemsSnapshotAfterItemRemoved(
        savedFormSnapshotRef,
        id,
      );
    }
    setItems((previous) =>
      setNoByOrder(previous.filter((item) => item.id !== id)),
    );
  };

  const moveItem = (id: number, direction: -1 | 1) => {
    setItems((previous) => {
      const currentIndex = previous.findIndex((item) => item.id === id);
      if (currentIndex < 0) {
        return previous;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const reordered = [...previous];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, moved);
      return setNoByOrder(reordered);
    });
  };

  const updateItem = (
    id: number,
    updater: (item: CorrectAnswerItem) => CorrectAnswerItem,
  ) => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const updateTextField = (
    id: number,
    field: "correctAnswer" | "wrongAnswer",
    value: string,
  ) => {
    const targetField = field === "correctAnswer" ? "correct" : "wrong";
    if (
      fieldErrorHighlightKey ===
      whackAMoleItemFieldTarget(id, targetField).highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    updateItem(id, (item) => ({ ...item, [field]: value }));
  };

  const setFileOnItem = (
    id: number,
    options: {
      file: File;
      fileNameField:
        | "imageCorrectAnswerFileName"
        | "soundCorrectAnswerFileName"
        | "imageWrongAnswerFileName"
        | "soundWrongAnswerFileName"
        | "hintImageFileName"
        | "hintAudioFileName";
      fileField:
        | "imageCorrectAnswerFile"
        | "soundCorrectAnswerFile"
        | "imageWrongAnswerFile"
        | "soundWrongAnswerFile"
        | "hintImageFile"
        | "hintAudioFile";
      urlField:
        | "imageCorrectAnswerUrl"
        | "soundCorrectAnswerUrl"
        | "imageWrongAnswerUrl"
        | "soundWrongAnswerUrl"
        | "hintImageUrl"
        | "hintAudioUrl";
      forceEnableHint?: boolean;
    },
  ) => {
    updateItem(id, (item) => ({
      ...item,
      [options.fileNameField]: options.file.name,
      [options.fileField]: options.file,
      [options.urlField]: null,
      ...(options.forceEnableHint ? { hasHint: true } : {}),
    }));
  };

  const getDeleteFieldLabel = (field: WhackAMoleMediaDeleteField) => {
    switch (field) {
      case "image_correct_answer":
        return tWhack("correctAnswerImage");
      case "sound_correct_answer":
        return tWhack("correctAnswerAudio");
      case "image_wrong_answer":
        return tWhack("incorrectAnswerImage");
      case "sound_wrong_answer":
        return tWhack("incorrectAnswerAudio");
      default:
        return tWhack("dataLabel");
    }
  };

  const requestDeleteMediaField = (
    itemId: number,
    field: WhackAMoleMediaDeleteField,
  ) => {
    setPendingMediaDelete({ itemId, field });
  };

  const clearMediaFieldFromItem = (
    itemId: number,
    field: WhackAMoleMediaDeleteField,
  ) => {
    updateItem(itemId, (item) => {
      if (field === "image_correct_answer") {
        return {
          ...item,
          imageCorrectAnswerFileName: "",
          imageCorrectAnswerFile: null,
          imageCorrectAnswerUrl: null,
        };
      }

      if (field === "sound_correct_answer") {
        return {
          ...item,
          soundCorrectAnswerFileName: "",
          soundCorrectAnswerFile: null,
          soundCorrectAnswerUrl: null,
        };
      }

      if (field === "image_wrong_answer") {
        return {
          ...item,
          imageWrongAnswerFileName: "",
          imageWrongAnswerFile: null,
          imageWrongAnswerUrl: null,
        };
      }

      return {
        ...item,
        soundWrongAnswerFileName: "",
        soundWrongAnswerFile: null,
        soundWrongAnswerUrl: null,
      };
    });
  };

  const confirmDeleteMediaField = async () => {
    if (!pendingMediaDelete) {
      return;
    }

    const { itemId, field } = pendingMediaDelete;
    const shouldCallDeleteApi =
      isUpdateMode && initialCorrectAnswerIdsRef.current.has(itemId);

    try {
      setIsDeletingMedia(true);

      if (shouldCallDeleteApi) {
        const response = await correctAnswerAPI.hardDeleteMediaFileAndHint({
          correctAnswerId: itemId,
          field,
        });

        if (response?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: `${tWhack("delete")} ${getDeleteFieldLabel(field)} ${tWhack("error")}`,
            type: "error",
          });
          return;
        }

        patchSavedWhackAMoleItemsSnapshotAfterMediaDeleted(
          savedFormSnapshotRef,
          itemId,
          field,
        );
      }

      clearMediaFieldFromItem(itemId, field);
      setPendingMediaDelete(null);
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const collectUploadTargets = (sourceItems: CorrectAnswerItem[]) => {
    const targets: UploadTarget[] = [];

    sourceItems.forEach((item, index) => {
      const timestamp = Date.now();

      if (item.imageCorrectAnswerFile && !item.imageCorrectAnswerUrl) {
        const ext = getFileExtension(item.imageCorrectAnswerFile.name);
        targets.push({
          key: `ca-${index}-image_correct_answer`,
          file: item.imageCorrectAnswerFile,
          fileName: `image_correct_answer_ca${item.id}_${timestamp}${ext}`,
          sectionName: "image_correct_answer",
          refId: `correct_answer_${item.id}`,
        });
      }

      if (item.soundCorrectAnswerFile && !item.soundCorrectAnswerUrl) {
        const ext = getFileExtension(item.soundCorrectAnswerFile.name);
        targets.push({
          key: `ca-${index}-sound_correct_answer`,
          file: item.soundCorrectAnswerFile,
          fileName: `sound_correct_answer_ca${item.id}_${timestamp}${ext}`,
          sectionName: "sound_correct_answer",
          refId: `correct_answer_${item.id}`,
        });
      }

      if (item.imageWrongAnswerFile && !item.imageWrongAnswerUrl) {
        const ext = getFileExtension(item.imageWrongAnswerFile.name);
        targets.push({
          key: `ca-${index}-image_wrong_answer`,
          file: item.imageWrongAnswerFile,
          fileName: `image_wrong_answer_ca${item.id}_${timestamp}${ext}`,
          sectionName: "image_wrong_answer",
          refId: `correct_answer_${item.id}`,
        });
      }

      if (item.soundWrongAnswerFile && !item.soundWrongAnswerUrl) {
        const ext = getFileExtension(item.soundWrongAnswerFile.name);
        targets.push({
          key: `ca-${index}-sound_wrong_answer`,
          file: item.soundWrongAnswerFile,
          fileName: `sound_wrong_answer_ca${item.id}_${timestamp}${ext}`,
          sectionName: "sound_wrong_answer",
          refId: `correct_answer_${item.id}`,
        });
      }
    });

    return targets;
  };

  const uploadPendingMedia = async (targets: UploadTarget[]) => {
    const uploadedUrlByKey = new Map<string, string>();

    if (targets.length === 0) {
      return uploadedUrlByKey;
    }

    const uploadResults = await Promise.allSettled(
      targets.map((target) =>
        uploadAPI
          .uploadMedia({
            file: target.file,
            fileName: target.fileName,
            sectionName: target.sectionName,
            ref_id: target.refId,
          })
          .then((response) => ({ target, response })),
      ),
    );

    for (const result of uploadResults) {
      if (result.status === "rejected") {
        throw new Error("upload_failed");
      }

      const response = result.value.response as {
        status?: string;
        url?: string;
      };
      if (response?.status === "failed" || !response?.url) {
        throw new Error("upload_failed");
      }

      uploadedUrlByKey.set(result.value.target.key, response.url);
    }

    return uploadedUrlByKey;
  };

  const applyUploadedUrlsToState = (
    sourceItems: CorrectAnswerItem[],
    uploadedUrlByKey: Map<string, string>,
  ) => {
    return sourceItems.map((item, index) => ({
      ...item,
      imageCorrectAnswerUrl:
        uploadedUrlByKey.get(`ca-${index}-image_correct_answer`) ??
        item.imageCorrectAnswerUrl,
      imageCorrectAnswerFile: uploadedUrlByKey.get(
        `ca-${index}-image_correct_answer`,
      )
        ? null
        : item.imageCorrectAnswerFile,
      soundCorrectAnswerUrl:
        uploadedUrlByKey.get(`ca-${index}-sound_correct_answer`) ??
        item.soundCorrectAnswerUrl,
      soundCorrectAnswerFile: uploadedUrlByKey.get(
        `ca-${index}-sound_correct_answer`,
      )
        ? null
        : item.soundCorrectAnswerFile,
      imageWrongAnswerUrl:
        uploadedUrlByKey.get(`ca-${index}-image_wrong_answer`) ??
        item.imageWrongAnswerUrl,
      imageWrongAnswerFile: uploadedUrlByKey.get(
        `ca-${index}-image_wrong_answer`,
      )
        ? null
        : item.imageWrongAnswerFile,
      soundWrongAnswerUrl:
        uploadedUrlByKey.get(`ca-${index}-sound_wrong_answer`) ??
        item.soundWrongAnswerUrl,
      soundWrongAnswerFile: uploadedUrlByKey.get(
        `ca-${index}-sound_wrong_answer`,
      )
        ? null
        : item.soundWrongAnswerFile,
      hintImageUrl: null,
      hintImageFile: null,
      hintAudioUrl: null,
      hintAudioFile: null,
    }));
  };

  const refreshFromServer = async () => {
    if (!previewUuid) {
      return;
    }

    const latestGameDetail =
      await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
    if (
      latestGameDetail &&
      (latestGameDetail as { status?: string }).status !== "failed"
    ) {
      applyLoadedGameDetail(
        latestGameDetail as WhackAMoleProps["initialGameDetail"],
      );
    }
  };

  const handleSubmitWhackAMole = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tWhack("error"),
        message: tWhack("noRecentGameData"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: tWhack("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      if (
        !Array.isArray(itemsWithResolvedUrls) ||
        itemsWithResolvedUrls.length === 0
      ) {
        openStatusPopUp(
          setStatusPopUp,
          createGameFieldErrorPopUp(
            tWhack("error"),
            tWhack("minMatchItems"),
            whackAMoleItemFieldTarget(items[0]?.id ?? 1, "correct"),
          ),
        );
        return;
      }

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const seenNos = new Set<number>();
      for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
        const item = itemsWithResolvedUrls[index];
        const no = index + 1;

        if (!Number.isInteger(no)) {
          setStatusPopUp({
            title: tWhack("error"),
            message: tWhack("invalidItemOrder"),
            type: "error",
          });
          return;
        }

        if (seenNos.has(no)) {
          setStatusPopUp({
            title: tWhack("error"),
            message: tWhack("duplicateItemOrder"),
            type: "error",
          });
          return;
        }
        seenNos.add(no);

        const hasCorrectAnswerMedia =
          Boolean(item.correctAnswer.trim()) ||
          Boolean(item.imageCorrectAnswerUrl) ||
          Boolean(item.soundCorrectAnswerUrl);
        if (!hasCorrectAnswerMedia) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tWhack("error"),
              `${tWhack("questionOrder")} ${no} ${tWhack("minContentRequired")}`,
              whackAMoleItemFieldTarget(item.id, "correct"),
            ),
          );
          return;
        }

        const hasWrongAnswerMedia =
          Boolean(item.wrongAnswer.trim()) ||
          Boolean(item.imageWrongAnswerUrl) ||
          Boolean(item.soundWrongAnswerUrl);
        if (!hasWrongAnswerMedia) {
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tWhack("error"),
              `${tWhack("matchOrder")} ${no} ${tWhack("minContentRequired")}`,
              whackAMoleItemFieldTarget(item.id, "wrong"),
            ),
          );
          return;
        }
      }

      const payloadRows = itemsWithResolvedUrls.map((item, index) => ({
        no: index + 1,
        expected_time: expectedTimeValue,
        correct_answer: item.correctAnswer.trim(),
        wrong_answer: item.wrongAnswer.trim(),
        hint: null,
        image_correct_answer: item.imageCorrectAnswerUrl || null,
        sound_correct_answer: item.soundCorrectAnswerUrl || null,
        image_wrong_answer: item.imageWrongAnswerUrl || null,
        sound_wrong_answer: item.soundWrongAnswerUrl || null,
        image_hint: null,
        sound_hint: null,
      }));

      const response = await correctAnswerAPI.createCorrectAnswer({
        gameId: createdGameData.id,
        skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
        skill_lang_nature: normalizedGameSkills.skill_lang_nature,
        skill_reading: normalizedGameSkills.skill_reading,
        skill_writing: normalizedGameSkills.skill_writing,
        audio_question: normalizedGameAudios.audio_question,
        audio_choice: normalizedGameAudios.audio_choice,
        audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
        audio_background: normalizedGameAudios.audio_background,
        correct_answers: payloadRows,
      });

      if (response?.status === "failed") {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("saveError"),
          type: "error",
        });
        return;
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      await refreshFromServer();
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tWhack("whackAMole"), "create"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateWhackAMole = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tWhack("error"),
        message: tWhack("noGameDataToUpdate"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
          gameId: createdGameData.id,
          ...(shouldUpdateExerciseName
            ? { exercise_name: trimmedExerciseName }
            : {}),
          ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
          ...(shouldUpdateDescription
            ? { description: trimmedDescription }
            : {}),
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: tWhack("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(items);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const itemsWithResolvedUrls = applyUploadedUrlsToState(
        items,
        uploadedUrlByKey,
      );

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("invalidCompetencyData"),
          type: "error",
        });
        return;
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tWhack("invalidAudioData"),
          type: "error",
        });
        return;
      }

      const changedSkillPayload: Partial<GameSkills> = {};
      if (
        normalizedGameSkills.skill_listening_speaking !==
        initialGameSkillsRef.current.skill_listening_speaking
      ) {
        changedSkillPayload.skill_listening_speaking =
          normalizedGameSkills.skill_listening_speaking;
      }
      if (
        normalizedGameSkills.skill_lang_nature !==
        initialGameSkillsRef.current.skill_lang_nature
      ) {
        changedSkillPayload.skill_lang_nature =
          normalizedGameSkills.skill_lang_nature;
      }
      if (
        normalizedGameSkills.skill_reading !==
        initialGameSkillsRef.current.skill_reading
      ) {
        changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
      }
      if (
        normalizedGameSkills.skill_writing !==
        initialGameSkillsRef.current.skill_writing
      ) {
        changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
      }

      const changedAudioPayload: Partial<GameAudios> = {};
      if (
        normalizedGameAudios.audio_question !==
        initialGameAudiosRef.current.audio_question
      ) {
        changedAudioPayload.audio_question =
          normalizedGameAudios.audio_question;
      }
      if (
        normalizedGameAudios.audio_choice !==
        initialGameAudiosRef.current.audio_choice
      ) {
        changedAudioPayload.audio_choice = normalizedGameAudios.audio_choice;
      }
      if (
        normalizedGameAudios.audio_assistant_voice !==
        initialGameAudiosRef.current.audio_assistant_voice
      ) {
        changedAudioPayload.audio_assistant_voice =
          normalizedGameAudios.audio_assistant_voice;
      }
      if (
        normalizedGameAudios.audio_background !==
        initialGameAudiosRef.current.audio_background
      ) {
        changedAudioPayload.audio_background =
          normalizedGameAudios.audio_background;
      }

      const currentIds = new Set<number>(
        itemsWithResolvedUrls.map((item) => item.id),
      );
      const deletedIds = Array.from(initialCorrectAnswerIdsRef.current).filter(
        (id) => !currentIds.has(id),
      );

      for (const deletedId of deletedIds) {
        const deleteResponse = await correctAnswerAPI.hardDeleteCorrectAnswer({
          correctAnswerId: deletedId,
        });
        if (deleteResponse?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: `${tWhack("deleteItemError")} ${deletedId} ${tWhack("error")}`,
            type: "error",
          });
          return;
        }
      }

      const existingRows = itemsWithResolvedUrls.filter((item) =>
        initialCorrectAnswerIdsRef.current.has(item.id),
      );
      const newRows = itemsWithResolvedUrls.filter(
        (item) => !initialCorrectAnswerIdsRef.current.has(item.id),
      );

      for (const row of existingRows) {
        const updateResponse = await correctAnswerAPI.updateCorrectAnswer({
          correctAnswerId: row.id,
          no: row.no,
          expected_time: expectedTimeValue,
          correct_answer: row.correctAnswer.trim(),
          wrong_answer: row.wrongAnswer.trim(),
          hint: null,
          image_correct_answer: row.imageCorrectAnswerUrl || null,
          sound_correct_answer: row.soundCorrectAnswerUrl || null,
          image_wrong_answer: row.imageWrongAnswerUrl || null,
          sound_wrong_answer: row.soundWrongAnswerUrl || null,
          image_hint: null,
          sound_hint: null,
          ...changedSkillPayload,
          ...changedAudioPayload,
        });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: `${tWhack("updateItemOrderError")} ${row.no} ${tWhack("error")}`,
            type: "error",
          });
          return;
        }
      }

      if (newRows.length > 0) {
        const createNewRowsResponse =
          await correctAnswerAPI.createCorrectAnswer({
            gameId: createdGameData.id,
            skill_listening_speaking:
              normalizedGameSkills.skill_listening_speaking,
            skill_lang_nature: normalizedGameSkills.skill_lang_nature,
            skill_reading: normalizedGameSkills.skill_reading,
            skill_writing: normalizedGameSkills.skill_writing,
            audio_question: normalizedGameAudios.audio_question,
            audio_choice: normalizedGameAudios.audio_choice,
            audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
            audio_background: normalizedGameAudios.audio_background,
            correct_answers: newRows.map((row) => ({
              no: row.no,
              expected_time: expectedTimeValue,
              correct_answer: row.correctAnswer.trim(),
              wrong_answer: row.wrongAnswer.trim(),
              hint: null,
              image_correct_answer: row.imageCorrectAnswerUrl || null,
              sound_correct_answer: row.soundCorrectAnswerUrl || null,
              image_wrong_answer: row.imageWrongAnswerUrl || null,
              sound_wrong_answer: row.soundWrongAnswerUrl || null,
              image_hint: null,
              sound_hint: null,
            })),
          });

        if (createNewRowsResponse?.status === "failed") {
          setStatusPopUp({
            title: tWhack("error"),
            message: tWhack("addItemError"),
            type: "error",
          });
          return;
        }
      }

      setItems(setNoByOrder(itemsWithResolvedUrls));
      await refreshFromServer();

      initialGameSkillsRef.current = normalizedGameSkills;
      initialGameAudiosRef.current = normalizedGameAudios;
      setIsUpdateMode(true);
      setStatusPopUp(buildGameSuccessStatus(tWhack("whackAMole"), "update"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseStatusPopUp = () => {
    closeStatusPopUp(setStatusPopUp, () => {
      void onCompleted;
    });
  };

  const handleStatusButtonClick = () => {
    if (isStatusConfirming || isSubmitting) {
      return;
    }

    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tWhack("updateStatusError"),
        message: tWhack("noGameDataToChangeStatus"),
        type: "error",
      });
      return;
    }

    setIsStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setIsStatusConfirmOpen(false);
  };

  const confirmStatusToggle = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tWhack("updateStatusError"),
        message: tWhack("noGameDataToChangeStatus"),
        type: "error",
      });
      setIsStatusConfirmOpen(false);
      return;
    }

    const nextStatus = !isPublished;
    const dialogContent = getStatusToggleDialogContent(
      isPublished,
      statusToggleDialogLabels,
    );

    setIsStatusConfirming(true);
    const response = await mainAPI.updateGameStatus({
      id: createdGameData.id,
      status: nextStatus,
    });
    setIsStatusConfirming(false);
    setIsStatusConfirmOpen(false);

    if (!response || response.status === "failed") {
      setStatusPopUp({
        title: tWhack("updateStatusError"),
        message: tWhack("changeStatusError"),
        type: "error",
      });
      return;
    }

    setIsPublished(nextStatus);
    setStatusPopUp({
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  const isExpectedTimeValid = useMemo(() => {
    const trimmed = expectedTime.trim();
    if (trimmed === "") {
      return false;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 0;
  }, [expectedTime]);
  const expectedTimeDisplay = useMemo(() => {
    if (!isExpectedTimeValid) {
      return "";
    }
    const value = Number(expectedTime.trim());
    if (value > 59) {
      const minutes = value / 60;
      const minuteText = Number.isInteger(minutes)
        ? String(minutes)
        : minutes.toFixed(2).replace(/\.?0+$/, "");
      return `${minuteText} ${tMain("expectedTimeMinuteUnit")}`;
    }
    return `${value} ${tMain("expectedTimeSecondUnit")}`;
  }, [expectedTime, isExpectedTimeValid, tMain]);
  const isSaveReady = useMemo(
    () => areWhackAMoleItemsSaveReady(items) && isExpectedTimeValid,
    [items, isExpectedTimeValid],
  );

  const currentFormSnapshot = useMemo(
    () =>
      createWhackAMoleItemsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        items,
      ),
    [exerciseName, suggestion, description, gameSkills, gameAudios, items],
  );
  const hasUnsavedChanges = useHasUnsavedGameFormChanges(
    isUpdateMode,
    currentFormSnapshot,
    savedFormSnapshotRef,
  );
  useReportGameEditorLeaveGuard(
    getShouldConfirmLeaveGameEditor(isUpdateMode, hasUnsavedChanges),
  );
  const canPreview = getCanGamePreview(
    isUpdateMode,
    previewUuid,
    hasUnsavedChanges,
  );

  const handleClickFinishButton = () => {
    if (isSubmitting || !isSaveReady) {
      return;
    }
    setIsConfirmSaveOpen(true);
  };

  const handleCancelConfirmSave = () => {
    setIsConfirmSaveOpen(false);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveOpen(false);
    if (isUpdateMode) {
      void handleUpdateWhackAMole();
      return;
    }
    void handleSubmitWhackAMole();
  };

  const handleOpenPreview = () => {
    if (!canPreview || hasUnsavedChanges || !isExpectedTimeValid) {
      if (!isExpectedTimeValid) {
        setStatusPopUp({
          title: tWhack("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
      } else if (hasUnsavedChanges) {
        setStatusPopUp({
          title: PREVIEW_UNSAVED_TITLE,
          message: PREVIEW_UNSAVED_MESSAGE,
          type: "info",
        });
      }
      return;
    }

    if (!previewUuid) {
      setStatusPopUp({
        title: tWhack("previewError"),
        message: tWhack("noGameUUIDForPreview"),
        type: "error",
      });
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  const handleClosePreview = () => {
    setPreviewIframeUrl(null);
  };

  return (
    <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
      <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
        <span className="font-semibold text-[#4E4E4E]">
          {tWhack("editContent")} &gt;{" "}
        </span>
        <span className="font-bold text-[#2b2b2b]">{tWhack("whackAMole")}</span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
            {tWhack("activityName")}
          </label>
          <input
            value={exerciseName}
            onChange={(event) => setExerciseName(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tWhack("noActivityName")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tWhack("details")}
          </label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tWhack("dragAndMatch")}
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tWhack("questionTitle")}
          </label>
          <input
            value={suggestion}
            onChange={(event) => setSuggestion(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            // placeholder={tWhack("dragAndMatch")}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-lg font-medium text-[#5b5b5b]">
            {tWhack("status")}:
          </span>
          <button
            type="button"
            onClick={handleStatusButtonClick}
            disabled={
              !createdGameData?.id || isSubmitting || isStatusConfirming
            }
            className={getPublishButtonClass(isPublished)}
          >
            {renderPublishStatusContent(isPublished, publishStatusLabels, 16)}
          </button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto p-3">
        <div className="w-full min-w-[640px] space-y-3 rounded-md border border-[#e0e0e0] bg-[#f9f9f9] p-3">
          <div className="grid w-full grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_96px] items-center gap-3 pb-2">
            <span></span>
            <div className="flex flex-col items-start gap-1">
              <span className="inline-block rounded-md bg-[#67E52C] px-10 py-1.5 text-lg font-semibold text-white">
                {tWhack("correct")}
              </span>
              <span className="text-sm mt-1 font-semibold">
                {tWhack("selectAnswers")}
              </span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="inline-block rounded-md bg-[#F54043] px-10 py-1.5 text-lg font-semibold text-white">
                {tWhack("incorrect")}
              </span>
              <span className="text-sm mt-1 font-semibold">
                {tWhack("avoidAnswers")}
              </span>
            </div>
            <span></span>
          </div>

          <div className="space-y-3">
            {items.map((item, index) => {
              const keywordImageId = `keyword-image-${item.id}`;
              const keywordAudioId = `keyword-audio-${item.id}`;
              const matchingImageId = `matching-image-${item.id}`;
              const matchingAudioId = `matching-audio-${item.id}`;
              return (
                <div key={item.id} className="flex flex-col">
                  <div className="grid w-full grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_96px] items-center gap-3">
                    <span className="shrink-0 text-lg font-semibold text-[#5a5a5a] text-center">
                      {index + 1}.
                    </span>

                    <div className="relative min-w-0">
                      <div
                        id={
                          whackAMoleItemFieldTarget(item.id, "correct").scrollId
                        }
                        className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(whackAMoleItemFieldTarget(item.id, "correct").highlightKey)}`}
                      >
                        <input
                          value={item.correctAnswer}
                          onChange={(event) =>
                            updateTextField(
                              item.id,
                              "correctAnswer",
                              event.target.value,
                            )
                          }
                          className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                        />
                        <div className="flex items-center gap-1 pl-2">
                          <MediaEditGroup
                            imageInputId={keywordImageId}
                            imageFile={item.imageCorrectAnswerFile}
                            imageUrl={item.imageCorrectAnswerUrl}
                            imageAlt={tWhack("correctAnswerImage")}
                            onImageChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "imageCorrectAnswerFileName",
                                fileField: "imageCorrectAnswerFile",
                                urlField: "imageCorrectAnswerUrl",
                              })
                            }
                            audioInputId={keywordAudioId}
                            audioFile={item.soundCorrectAnswerFile}
                            audioUrl={item.soundCorrectAnswerUrl}
                            onAudioChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "soundCorrectAnswerFileName",
                                fileField: "soundCorrectAnswerFile",
                                urlField: "soundCorrectAnswerUrl",
                              })
                            }
                            onImageDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "image_correct_answer",
                              )
                            }
                            onAudioDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "sound_correct_answer",
                              )
                            }
                            disableDelete={isSubmitting || isDeletingMedia}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="relative min-w-0">
                      <div
                        id={
                          whackAMoleItemFieldTarget(item.id, "wrong").scrollId
                        }
                        className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getFieldBorderClass(whackAMoleItemFieldTarget(item.id, "wrong").highlightKey)}`}
                      >
                        <input
                          value={item.wrongAnswer}
                          onChange={(event) =>
                            updateTextField(
                              item.id,
                              "wrongAnswer",
                              event.target.value,
                            )
                          }
                          className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                        />
                        <div className="flex items-center gap-1 pl-2">
                          <MediaEditGroup
                            imageInputId={matchingImageId}
                            imageFile={item.imageWrongAnswerFile}
                            imageUrl={item.imageWrongAnswerUrl}
                            imageAlt={tWhack("incorrectAnswerImage")}
                            onImageChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "imageWrongAnswerFileName",
                                fileField: "imageWrongAnswerFile",
                                urlField: "imageWrongAnswerUrl",
                              })
                            }
                            audioInputId={matchingAudioId}
                            audioFile={item.soundWrongAnswerFile}
                            audioUrl={item.soundWrongAnswerUrl}
                            onAudioChange={(file) =>
                              setFileOnItem(item.id, {
                                file,
                                fileNameField: "soundWrongAnswerFileName",
                                fileField: "soundWrongAnswerFile",
                                urlField: "soundWrongAnswerUrl",
                              })
                            }
                            onImageDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "image_wrong_answer",
                              )
                            }
                            onAudioDelete={() =>
                              requestDeleteMediaField(
                                item.id,
                                "sound_wrong_answer",
                              )
                            }
                            disableDelete={isSubmitting || isDeletingMedia}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex w-full min-w-0 items-center justify-center gap-3 self-start">
                      <div className="flex shrink-0 flex-col items-center gap-0 text-[#555]">
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, -1)}
                          disabled={index === 0}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tWhack("moveUp")}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItem(item.id, 1)}
                          disabled={index === items.length - 1}
                          className="cursor-pointer leading-none hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                          title={tWhack("moveDown")}
                        >
                          ↓
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteItemId(item.id)}
                        disabled={!canRemoveItem}
                        className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                        title={
                          canRemoveItem
                            ? tWhack("delete")
                            : `${tWhack("minRequired")} ${MIN_ITEMS} ${tWhack("item")}`
                        }
                      >
                        <MdDeleteOutline size={22} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 ml-3">
            <button
              type="button"
              onClick={addItem}
              disabled={!canAddItem}
              className="ml-0 md:ml-10 rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {tWhack("addItem")}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 w-full p-3">
        <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
          {tMain("expectedTimeLabel")}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={expectedTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setExpectedTime(nextValue);
              }
            }}
            required
            inputMode="numeric"
            className="h-10 w-full max-w-[360px] rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tMain("expectedTimePlaceholder")}
          />
          {expectedTimeDisplay ? (
            <span className="text-base font-semibold text-[#4a4a4a]">
              {expectedTimeDisplay}
            </span>
          ) : null}
        </div>
      </div>

      <div className="w-full p-3">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <TbCategory2 className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tWhack("competency")}
                </div>
                <div className="text-sm text-[#888]">
                  {tWhack("competencySubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <GiBrain className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tWhack("listeningSpeakingThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_listening_speaking}
                  onChange={(event) =>
                    updateGameSkill(
                      "skill_listening_speaking",
                      event.target.checked,
                    )
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlineBookOpen className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tWhack("readingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_reading}
                  onChange={(event) =>
                    updateGameSkill("skill_reading", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <MdOutlineChat className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tWhack("understandingThaiLanguage")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_lang_nature}
                  onChange={(event) =>
                    updateGameSkill("skill_lang_nature", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlinePencilAlt className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tWhack("writingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_writing}
                  onChange={(event) =>
                    updateGameSkill("skill_writing", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <HiOutlineVolumeUp className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tWhack("sound")}
                </div>
                <div className="text-sm text-[#888]">
                  {tWhack("soundSubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <HiOutlineMicrophone className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tWhack("narration")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_question}
                      onChange={(event) =>
                        updateGameAudio("audio_question", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tWhack("muteNarration")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_choice}
                      onChange={(event) =>
                        updateGameAudio("audio_choice", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tWhack("muteOptionNarration")}
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <IoGameControllerOutline className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tWhack("gameSound")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_assistant_voice}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_assistant_voice",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tWhack("muteAssistant")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_background}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_background",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tWhack("muteBackground")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
        {isUpdateMode && previewUuid ? (
          <button
            type="button"
            onClick={handleOpenPreview}
            disabled={hasUnsavedChanges || isSubmitting || !isExpectedTimeValid}
            className={GAME_PREVIEW_BUTTON_CLASS}
            title={
              hasUnsavedChanges
                ? PREVIEW_BUTTON_DISABLED_TITLE
                : tWhack("preview")
            }
          >
            {tWhack("preview")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleClickFinishButton}
          disabled={isSubmitting || !isSaveReady}
          className="cursor-pointer rounded bg-[#f4d735] px-7 py-2 text-lg font-semibold text-[#4a4a4a] shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#e0c91a] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? tWhack("saving") : tWhack("save")}
        </button>
      </div>

      {isConfirmSaveOpen ? (
        <ConfirmPopUp
          title={tWhack("confirmSave")}
          message={tWhack("savePrompt")}
          cancelText={tWhack("cancel")}
          confirmText={tWhack("confirm")}
          onCancel={handleCancelConfirmSave}
          onConfirm={handleConfirmSave}
          isConfirming={isSubmitting}
        />
      ) : null}

      {isStatusConfirmOpen ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmTitle
          }
          message={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmMessage
          }
          confirmText={tWhack("confirm")}
          cancelText={tWhack("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {pendingDeleteItemId !== null ? (
        <ConfirmPopUp
          title={tWhack("confirmDelete")}
          message={tWhack("deletePrompt")}
          cancelText={tWhack("cancel")}
          confirmText={tWhack("confirm")}
          onCancel={() => setPendingDeleteItemId(null)}
          onConfirm={async () => {
            await removeItem(pendingDeleteItemId);
            setPendingDeleteItemId(null);
          }}
        />
      ) : null}

      {pendingMediaDelete !== null ? (
        <ConfirmPopUp
          title={tWhack("confirmDelete")}
          message={`${tWhack("wantToDelete")} ${getDeleteFieldLabel(pendingMediaDelete.field)} ${tWhack("or")}`}
          cancelText={tWhack("cancel")}
          confirmText={tWhack("confirm")}
          onCancel={() => {
            if (isDeletingMedia) {
              return;
            }
            setPendingMediaDelete(null);
          }}
          onConfirm={() => {
            void confirmDeleteMediaField();
          }}
          isConfirming={isDeletingMedia}
        />
      ) : null}

      {statusPopUp ? (
        <StatusPopUp
          title={statusPopUp.title}
          message={statusPopUp.message}
          type={statusPopUp.type}
          onClose={handleCloseStatusPopUp}
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle={tWhack("previewTitle")}
          onClose={handleClosePreview}
          closeVariant="danger"
          shellVariant="editor"
        />
      ) : null}
    </section>
  );
}
